import React, { useCallback, useMemo } from 'react';
import { useReactFlow } from '@xyflow/react';
import { useNodeStore, selectSelectedNodeIds } from '@/stores/node-store';
import { useShallow } from 'zustand/react/shallow';
import { CustomEdge as CustomEdgeType } from '@/types/edge-types';
import { CustomNode } from '@/types/node-types';
import {
  HANDLE_STYLING,
  HandleTypes,
  getWorkflowEdgeStrokeColor,
} from '@/constants/node-styling';
import { useWorkflowStore } from '@/stores/workflow-store';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import { isArtifactEdge } from '@/pages/workflow/helper/artifact-node-manager';
import {
  getConditionalBranchLabel,
  getSelectedCase,
  isConditionalEdge,
} from '@/pages/workflow/helper/functional-control-flow';
// --- repeat loop (Phase 3) ---------------------------------------------------
import {
  isRepeatNode,
  REPEAT_BACK_EDGE_ROLE,
  REPEAT_CONDITION_ROLE,
} from '@/pages/workflow/helper/repeat-control';
import { ConnectionType } from '@/types/edge-types';

/**
 * A loop edge's role, from the graph rather than from `data.repeat_role`
 * alone, so edges drawn by hand (or by the backend's exports) render the
 * same way as edges the panel wrote.
 */
export const repeatRoleOf = (
  edge: CustomEdgeType | undefined,
  nodes: readonly CustomNode[],
): typeof REPEAT_BACK_EDGE_ROLE | typeof REPEAT_CONDITION_ROLE | null => {
  if (!edge) return null;
  const declared = (edge.data as { repeat_role?: string } | undefined)
    ?.repeat_role;
  if (
    declared === REPEAT_BACK_EDGE_ROLE ||
    edge.edge_type === ConnectionType.REPEAT
  ) {
    return REPEAT_BACK_EDGE_ROLE;
  }
  if (declared === REPEAT_CONDITION_ROLE) return REPEAT_CONDITION_ROLE;
  const target = nodes.find((node) => node.id === edge.target) as
    | CustomNode
    | undefined;
  if (
    isRepeatNode(target?.data?.struct) &&
    (edge.target_param_id === 'input' ||
      edge.edge_type === ConnectionType.PORT_TO_PARAM ||
      edge.edge_type === ConnectionType.PORT_TO_PORT)
  ) {
    return REPEAT_CONDITION_ROLE;
  }
  return null;
};
// --- end repeat loop ---------------------------------------------------------
// --- for each (Phase 8c) -----------------------------------------------------
import {
  isMapCollectEdge,
  isMapItemEdge,
} from '@/pages/workflow/helper/for-each-region';

/** A For-each body edge's role: "each item" (Map → first step) or "collect" (last step → Map). */
export const forEachRoleOf = (
  edge: CustomEdgeType | undefined,
): 'item' | 'collect' | null => {
  if (!edge) return null;
  if (isMapCollectEdge(edge)) return 'collect';
  if (isMapItemEdge(edge)) return 'item';
  return null;
};
// --- end for each ------------------------------------------------------------

export function useEdgeInteraction(id: string, selected?: boolean) {
  const selectedNodeIds = useNodeStore(useShallow(selectSelectedNodeIds));
  const { onEdgesDelete, edges, nodes } = useNodeStore();
  const { setEdges } = useReactFlow();
  const currentWorkflow = useWorkflowStore((s) => s.currentWorkflow);
  const { isWorkflowLockingEnabled } = useLaunchDarkly();
  const isWorkflowLocked = isWorkflowLockingEnabled
    ? (currentWorkflow?.is_locked ?? false)
    : false;

  const currentEdge = useMemo(
    () => edges.find((e) => e.id === id) as CustomEdgeType | undefined,
    [id, edges],
  );

  const strokeColor = useMemo(() => {
    if (!currentEdge) {
      return HANDLE_STYLING[HandleTypes.PARAM].color;
    }
    const sourceNode = nodes.find((n) => n.id === currentEdge.source) as
      | CustomNode
      | undefined;
    const targetNode = nodes.find((n) => n.id === currentEdge.target) as
      | CustomNode
      | undefined;
    return getWorkflowEdgeStrokeColor(currentEdge, sourceNode, targetNode);
  }, [currentEdge, nodes]);

  const conditionalState = useMemo(() => {
    if (!currentEdge || !isConditionalEdge(currentEdge)) {
      return {
        isConditional: false,
        branchLabel: null,
        isSelectedBranch: false,
        hasSelectedBranch: false,
      };
    }
    const sourceNode = nodes.find((node) => node.id === currentEdge.source) as
      | CustomNode
      | undefined;
    const selectedCase = getSelectedCase(sourceNode?.data?.result);
    const branchLabel = getConditionalBranchLabel(currentEdge);
    return {
      isConditional: true,
      branchLabel,
      isSelectedBranch: Boolean(selectedCase) && selectedCase === branchLabel,
      hasSelectedBranch: Boolean(selectedCase),
    };
  }, [currentEdge, nodes]);

  // --- repeat loop (Phase 3) ---
  const repeatRole = useMemo(
    () => repeatRoleOf(currentEdge, nodes),
    [currentEdge, nodes],
  );
  // --- end repeat loop ---
  const forEachRole = useMemo(() => forEachRoleOf(currentEdge), [currentEdge]);

  const showDeleteToolbar = useMemo(
    () =>
      !!selected &&
      selectedNodeIds.length === 0 &&
      !isWorkflowLocked &&
      !(currentEdge ? isArtifactEdge(currentEdge) : false),
    [selected, selectedNodeIds.length, isWorkflowLocked, currentEdge],
  );

  const handleEdgeClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (isWorkflowLocked) return;
      setEdges((eds) =>
        eds.map((edge) => ({
          ...edge,
          selected: edge.id === id,
        })),
      );
    },
    [id, isWorkflowLocked, setEdges],
  );

  const handleDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (isWorkflowLocked) return;
      const edgeToDelete = edges.find((e) => e.id === id);
      if (edgeToDelete) {
        onEdgesDelete([edgeToDelete as CustomEdgeType]);
      }
    },
    [id, isWorkflowLocked, edges, onEdgesDelete],
  );

  return {
    currentEdge,
    nodes,
    strokeColor,
    ...conditionalState,
    repeatRole,
    forEachRole,
    showDeleteToolbar,
    handleEdgeClick,
    handleDeleteClick,
  };
}
